// Companion imports
import {
  type AgricobotCompanionAsset,
  AgricobotCompanionMessageTypes,
} from '@/modules/agricobots/assets/AgricobotCompanion';
import { AgricobotCompanionServices } from '@/modules/agricobots/services/AgricobotCompanionServices';
import type { AgricobotCompanionHeartbeatDataArray } from '@/modules/agricobots/types/CompanionHeartbeatArray';
import type { AgricobotCompanionTelemetryDataArray } from '@/modules/agricobots/types/CompanionTelemetryArray';
import { supabase } from '@/service/supabaseClient';
import {
  BOOLEAN_SIGNAL_IDS,
  SIGNAL_EXPORT_PREFIX,
  SIGNAL_IDS,
  type SignalValue,
  prettifySignalId,
  readSignalsPayload,
  signalNumber,
} from '@/types/signals';
import { TimeSync } from '@/utils/timesync/timesync';
import {
  formatBooleanStatus,
  formatDecimal,
  formatPercentage,
  formatRpm,
  formatSmartValue,
  formatVoltage,
} from '../utils/formatUtils';
import { AssetTypes } from './AssetTypes';
import { GenericAsset, type InboundRecord } from './GenericAsset';

// Enum for message types specific to AgricobotAsset
export enum AgricobotMessageTypes {
  TELEMETRY_DATA = 'telemetry_data',
  HEARTBEAT = 'heartbeat',
  COMPRESSED_IMAGE = 'compressed_image',
  COMPANION_HEARTBEAT = 'companion_heartbeat',
  COMPANION_TELEMETRY_DATA = 'companion_telemetry',
  /**
   * Valori letti da topic ROS che non fanno parte della telemetria, scritti
   * dall'injector in una riga a parte. Tipo nuovo di proposito: una dashboard
   * che non lo conosce lo ignora (vedi types/signals.ts).
   */
  SIGNALS = 'signals',
  // Add other Agricobot-specific message types here
}

export enum AgricobotOnlyMessageTypes {
  TELEMETRY_DATA = 'telemetry_data',
  HEARTBEAT = 'heartbeat',
  COMPRESSED_IMAGE = 'compressed_image',
  // Add other Agricobot-specific message types here
}

// New enum for exportable message types (excludes compressed_image and heartbeat)
export enum AgricobotExportableMessageTypes {
  TELEMETRY_DATA = 'telemetry_data',
  // Add other exportable message types here (exclude COMPRESSED_IMAGE and HEARTBEAT)
}

export type AgricobotHeartbeatData = {
  rbt_id: string | null;
  rbt_model: string | null;
  software_version: string | null;
  frame_id: string | null;
  status: string | null;
};

export type AgricobotTelemetryData = {
  frame_id: string | null;
  timestamp: Date | null;
  pos_x_ecef: number | null;
  pos_y_ecef: number | null;
  pos_z_ecef: number | null;
  vel_x_ms: number | null;
  rot_x: number | null;
  rot_y: number | null;
  rot_z: number | null;
  rot_w: number | null;
  solution_type: number | null;
  vision_status: boolean | null;
  anti_collision_status: number | null;
  identified_obstacle_type: string | null;
  engine_status: boolean | null;
  engine_rpm: number | null;
  /**
   * Engine load as a percentage of the maximum the engine can deliver.
   *
   * Optional because the machines do not publish it yet: the field is here so
   * the dashboard can read it the day they do, and until then every consumer
   * sees null and says so. Filled in by the dev simulator, which is the only
   * place it currently comes from.
   */
  engine_torque_percent?: number | null;
  /**
   * Power take-off speed in revolutions per minute.
   *
   * Optional for the same reason as the torque: the machines do not publish it
   * yet. Zero is a real reading here and means the shaft is not turning, which
   * is why the panel greys the whole meter at zero instead of showing an empty
   * bar - an idle power take-off is not a low reading, it is an absent one.
   */
  pto_rpm?: number | null;
  /**
   * Engine coolant temperature in degrees Celsius.
   *
   * Optional like the torque and the power take-off: not published yet. Unlike
   * them it has a wrong end at both extremes - too cold is a real state, not
   * merely a low number - which is why the panel draws it against a range
   * instead of filling a bar.
   */
  coolant_temp_c?: number | null;
  /** Hydraulic oil temperature in degrees Celsius. Also not published yet. */
  hydraulic_oil_temp_c?: number | null;
  fuel_level_percent: number | null;
  ecu_voltage_v: number | null;
  water_tank_level_percent: number | null;
  attachment_type: string | null;
  attachment_status: boolean | null;
  sprayer_left_status: boolean | null;
  sprayer_right_status: boolean | null;
  pump_pressure_bar: number | null;
  flow_rate_l_min: number | null;
  plan_name: string | null;
  plan_status: number | null;
  plan_completion_percent: number | null;
  anomaly_id: number | null;
  anomaly_type: string | null;
};

export type AgricobotCameraData = {
  frame_id: string | null;
  timestamp: number | null;
  format: string | null;
  data_size_bytes: number | null;
  image_width_px: number | null;
  image_height_px: number | null;
  data_payload: string | null; // Assuming base64 encoded string for [] uint8
  data_hash: string | null;
  content_type: string | null;
  compression_ratio: number | null;
  jpeg_quality_percent: number | null;
};

export class AgricobotAsset extends GenericAsset {
  protected supportedMessageTypes: string[] = [
    AgricobotMessageTypes.HEARTBEAT,
    AgricobotMessageTypes.TELEMETRY_DATA,
    AgricobotMessageTypes.COMPRESSED_IMAGE,
  ];

  public heartbeatData: AgricobotHeartbeatData;
  public telemetryData: AgricobotTelemetryData;
  public cameraData: AgricobotCameraData[];
  public companionHeartbeatData: AgricobotCompanionHeartbeatDataArray;
  public companionTelemetryData: AgricobotCompanionTelemetryDataArray;
  /**
   * Ultimo valore noto di ogni segnale, per id.
   *
   * Vuoto finche' la macchina non ne pubblica: un segnale assente e un segnale
   * a zero sono due cose diverse, e chi legge deve poterle distinguere.
   */
  public signals: Record<string, SignalValue>;
  private companions: AgricobotCompanionAsset[];

  // Timestamps
  public lastHeartbeat: Date | null;
  public lastTelemetry: Date | null;
  public lastCamera: Date | null;
  public lastCompanionHeartbeat: Date | null;
  public lastCompanionTelemetry: Date | null;
  public lastSignals: Date | null;

  // Cached latest camera image URL (blob or placeholder) and received time
  private _latestCameraImageUrl: string | null = null;

  constructor(InboundRecords: InboundRecord[]) {
    // Sort records by timestamp in descending order to process the newest first
    InboundRecords.sort(
      (a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime(),
    );

    super(InboundRecords);

    if (InboundRecords.length > 0) {
      this.lastMessageReceived = new Date(InboundRecords[0].received_at);
    }

    /* ================================================
     * INITIALIZATION PART
     * ================================================ */

    // Initialize timestamps
    this.lastHeartbeat = null;
    this.lastTelemetry = null;
    this.lastCamera = null;
    this.lastCompanionHeartbeat = null;
    this.lastCompanionTelemetry = null;
    this.lastSignals = null;
    this.signals = {};
    this.companions = [];

    // Initialize heartbeatData with default null values
    this.heartbeatData = {
      rbt_id: null,
      rbt_model: null,
      software_version: null,
      frame_id: null,
      status: null,
    };

    // Initialize telemetryData with default null values
    this.telemetryData = {
      frame_id: null,
      timestamp: null,
      pos_x_ecef: null,
      pos_y_ecef: null,
      pos_z_ecef: null,
      vel_x_ms: null,
      rot_x: null,
      rot_y: null,
      rot_z: null,
      rot_w: null,
      solution_type: null,
      vision_status: null,
      anti_collision_status: null,
      identified_obstacle_type: null,
      engine_status: null,
      engine_rpm: null,
      engine_torque_percent: null,
      pto_rpm: null,
      coolant_temp_c: null,
      hydraulic_oil_temp_c: null,
      fuel_level_percent: null,
      ecu_voltage_v: null,
      water_tank_level_percent: null,
      attachment_type: null,
      attachment_status: null,
      sprayer_left_status: null,
      sprayer_right_status: null,
      pump_pressure_bar: null,
      flow_rate_l_min: null,
      plan_name: null,
      plan_status: null,
      plan_completion_percent: null,
      anomaly_id: null,
      anomaly_type: null,
    };

    // Initialize cameraData as an array to support multiple cameras (by frame_id)
    this.cameraData = [];

    // Initialize companionHeartbeatData with default null values
    this.companionHeartbeatData = {
      frame_id: null,
      robot_id: null,
      companion_ids: null,
      companion_models: null,
      companion_statuses: null,
      companion_sftw_versions: null,
      companion_count: null,
    };

    // Initialize companionTelemetryData with default null values
    this.companionTelemetryData = {
      frame_id: null,
      robot_id: null,
      timestamp: null,
      companion_ids: null,
      pos_x_ecef_array: null,
      pos_y_ecef_array: null,
      pos_z_ecef_array: null,
      vel_x_ms_array: null,
      rot_x_array: null,
      rot_y_array: null,
      rot_z_array: null,
      rot_w_array: null,
      solution_type_array: null,
      vision_status_array: null,
      anti_collision_status_array: null,
      identified_obstacle_type_array: null,
      engine_status_array: null,
      engine_rpm_array: null,
      fuel_level_percent_array: null,
      ecu_voltage_v_array: null,
      water_tank_level_percent_array: null,
      attachment_type_array: null,
      attachment_status_array: null,
      sprayer_left_status_array: null,
      sprayer_right_status_array: null,
      pump_pressure_bar_array: null,
      flow_rate_l_min_array: null,
      plan_name_array: null,
      plan_status_array: null,
      plan_completion_percent_array: null,
      anomaly_id_array: null,
      anomaly_type_array: null,
      companion_count: null,
    };

    /* ================================================
     * ASSIGNATION PART
     * ================================================ */

    let latestTelemetryRecord: InboundRecord | undefined;
    let latestCameraRecord: InboundRecord | undefined;
    let latestHeartbeatRecord: InboundRecord | undefined;
    let latestCompanionHeartbeatRecord: InboundRecord | undefined;
    let latestCompanionTelemetryRecord: InboundRecord | undefined;
    let latestSignalsRecord: InboundRecord | undefined;

    for (const record of InboundRecords) {
      if (record.message_type === AgricobotMessageTypes.HEARTBEAT && !latestHeartbeatRecord) {
        latestHeartbeatRecord = record;
      } else if (
        record.message_type === AgricobotMessageTypes.COMPRESSED_IMAGE &&
        !latestCameraRecord
      ) {
        latestCameraRecord = record;
      } else if (
        record.message_type === AgricobotMessageTypes.TELEMETRY_DATA &&
        !latestTelemetryRecord
      ) {
        latestTelemetryRecord = record;
      } else if (
        record.message_type === AgricobotMessageTypes.COMPANION_HEARTBEAT &&
        !latestCompanionHeartbeatRecord
      ) {
        latestCompanionHeartbeatRecord = record;
      } else if (
        record.message_type === AgricobotMessageTypes.COMPANION_TELEMETRY_DATA &&
        !latestCompanionTelemetryRecord
      ) {
        latestCompanionTelemetryRecord = record;
      } else if (record.message_type === AgricobotMessageTypes.SIGNALS && !latestSignalsRecord) {
        latestSignalsRecord = record;
      }

      // If we have found the latest record for all supported types, we can stop
      if (
        latestTelemetryRecord &&
        latestCameraRecord &&
        latestHeartbeatRecord &&
        latestCompanionHeartbeatRecord &&
        latestCompanionTelemetryRecord &&
        latestSignalsRecord
      ) {
        break;
      }
    }

    if (latestHeartbeatRecord) {
      const heartbeat = latestHeartbeatRecord.payload as AgricobotHeartbeatData;
      this.heartbeatData.rbt_id = heartbeat.rbt_id;
      this.heartbeatData.rbt_model = heartbeat.rbt_model;
      this.heartbeatData.software_version = heartbeat.software_version;
      this.heartbeatData.frame_id = heartbeat.frame_id;
      this.heartbeatData.status = heartbeat.status;
      this.lastHeartbeat = new Date(latestHeartbeatRecord.received_at);
    }

    if (latestTelemetryRecord) {
      const tel = this.telemetryData;
      const payloadObject = latestTelemetryRecord.payload as {
        frame_id?: string;
        timestamp?: number;
        pos_x_ecef?: number;
        pos_y_ecef?: number;
        pos_z_ecef?: number;
        vel_x_ms?: number;
        rot_x?: number;
        rot_y?: number;
        rot_z?: number;
        rot_w?: number;
        solution_type?: number;
        vision_status?: boolean;
        anti_collision_status?: number;
        identified_obstacle_type?: string;
        engine_status?: boolean;
        engine_rpm?: number;
        engine_torque_percent?: number;
        pto_rpm?: number;
        coolant_temp_c?: number;
        hydraulic_oil_temp_c?: number;
        fuel_level_percent?: number;
        ecu_voltage_v?: number;
        water_tank_level_percent?: number;
        attachment_type?: string;
        attachment_status?: boolean;
        sprayer_left_status?: boolean;
        sprayer_right_status?: boolean;
        pump_pressure_bar?: number;
        flow_rate_l_min?: number;
        plan_name?: string;
        plan_status?: number;
        plan_completion_percent?: number;
        anomaly_id?: number;
        anomaly_type?: string;
        TIMESTAMP?: {
          FrameId?: string;
          Package?: number;
          Seq?: number;
          Stamp?: string;
        };
      };

      tel.frame_id = payloadObject.frame_id ?? tel.frame_id;
      tel.pos_x_ecef = payloadObject.pos_x_ecef ?? tel.pos_x_ecef;
      tel.pos_y_ecef = payloadObject.pos_y_ecef ?? tel.pos_y_ecef;
      tel.pos_z_ecef = payloadObject.pos_z_ecef ?? tel.pos_z_ecef;
      tel.vel_x_ms = payloadObject.vel_x_ms ?? tel.vel_x_ms;
      tel.rot_x = payloadObject.rot_x ?? tel.rot_x;
      tel.rot_y = payloadObject.rot_y ?? tel.rot_y;
      tel.rot_z = payloadObject.rot_z ?? tel.rot_z;
      tel.rot_w = payloadObject.rot_w ?? tel.rot_w;
      tel.solution_type = payloadObject.solution_type ?? tel.solution_type;
      tel.vision_status = payloadObject.vision_status ?? tel.vision_status;
      tel.anti_collision_status = payloadObject.anti_collision_status ?? tel.anti_collision_status;
      tel.identified_obstacle_type =
        payloadObject.identified_obstacle_type ?? tel.identified_obstacle_type;
      tel.engine_status = payloadObject.engine_status ?? tel.engine_status;
      tel.engine_rpm = payloadObject.engine_rpm ?? tel.engine_rpm;
      tel.engine_torque_percent = payloadObject.engine_torque_percent ?? tel.engine_torque_percent;
      tel.pto_rpm = payloadObject.pto_rpm ?? tel.pto_rpm;
      tel.coolant_temp_c = payloadObject.coolant_temp_c ?? tel.coolant_temp_c;
      tel.hydraulic_oil_temp_c =
        payloadObject.hydraulic_oil_temp_c ?? tel.hydraulic_oil_temp_c;
      tel.fuel_level_percent = payloadObject.fuel_level_percent ?? tel.fuel_level_percent;
      tel.ecu_voltage_v = payloadObject.ecu_voltage_v ?? tel.ecu_voltage_v;
      tel.water_tank_level_percent =
        payloadObject.water_tank_level_percent ?? tel.water_tank_level_percent;
      tel.attachment_type = payloadObject.attachment_type ?? tel.attachment_type;
      tel.attachment_status = payloadObject.attachment_status ?? tel.attachment_status;
      tel.sprayer_left_status = payloadObject.sprayer_left_status ?? tel.sprayer_left_status;
      tel.sprayer_right_status = payloadObject.sprayer_right_status ?? tel.sprayer_right_status;
      tel.pump_pressure_bar = payloadObject.pump_pressure_bar ?? tel.pump_pressure_bar;
      tel.flow_rate_l_min = payloadObject.flow_rate_l_min ?? tel.flow_rate_l_min;
      tel.plan_name = payloadObject.plan_name ?? tel.plan_name;
      tel.plan_status = payloadObject.plan_status ?? tel.plan_status;
      tel.plan_completion_percent =
        payloadObject.plan_completion_percent ?? tel.plan_completion_percent;
      tel.anomaly_id = payloadObject.anomaly_id ?? tel.anomaly_id;
      tel.anomaly_type = payloadObject.anomaly_type ?? tel.anomaly_type;

      if (payloadObject.TIMESTAMP?.Stamp !== undefined) {
        tel.timestamp = new Date(payloadObject.TIMESTAMP.Stamp);
      } else if (payloadObject.timestamp !== undefined) {
        tel.timestamp = new Date(payloadObject.timestamp);
      }
      if (payloadObject.TIMESTAMP?.FrameId !== undefined) {
        tel.frame_id = payloadObject.TIMESTAMP.FrameId;
      } else if (payloadObject.frame_id !== undefined) {
        tel.frame_id = payloadObject.frame_id;
      }

      this.lastTelemetry = new Date(latestTelemetryRecord.received_at);
    }

    if (latestSignalsRecord) {
      this.signals = readSignalsPayload(latestSignalsRecord.payload);
      this.lastSignals = new Date(latestSignalsRecord.received_at);
    }

    if (latestCameraRecord) {
      const imagePayload = latestCameraRecord.payload as {
        frame_id?: string;
        timestamp?: number;
        format?: string;
        data_size_bytes?: number;
        image_width_px?: number;
        image_height_px?: number;
        data_payload?: string;
        data_hash?: string;
        content_type?: string;
        compression_ratio?: number;
        jpeg_quality_percent?: number;
      };
      this._upsertCameraDataFromPayload(imagePayload);
      this.lastCamera = new Date(latestCameraRecord.received_at);
    }

    if (latestCompanionHeartbeatRecord) {
      const companionHeartbeat =
        latestCompanionHeartbeatRecord.payload as AgricobotCompanionHeartbeatDataArray;
      this.companionHeartbeatData.frame_id = companionHeartbeat.frame_id;
      this.companionHeartbeatData.robot_id = companionHeartbeat.robot_id;
      this.companionHeartbeatData.companion_ids = companionHeartbeat.companion_ids;
      this.companionHeartbeatData.companion_models = companionHeartbeat.companion_models;
      this.companionHeartbeatData.companion_statuses = companionHeartbeat.companion_statuses;
      this.companionHeartbeatData.companion_sftw_versions =
        companionHeartbeat.companion_sftw_versions;
      this.companionHeartbeatData.companion_count = companionHeartbeat.companion_count;
      this.lastCompanionHeartbeat = new Date(latestCompanionHeartbeatRecord.received_at);
    }

    if (latestCompanionTelemetryRecord) {
      const companionTelemetry =
        latestCompanionTelemetryRecord.payload as AgricobotCompanionTelemetryDataArray;
      this.companionTelemetryData.frame_id = companionTelemetry.frame_id;
      this.companionTelemetryData.robot_id = companionTelemetry.robot_id;
      this.companionTelemetryData.timestamp = companionTelemetry.timestamp;
      this.companionTelemetryData.companion_ids = companionTelemetry.companion_ids;
      this.companionTelemetryData.pos_x_ecef_array = companionTelemetry.pos_x_ecef_array;
      this.companionTelemetryData.pos_y_ecef_array = companionTelemetry.pos_y_ecef_array;
      this.companionTelemetryData.pos_z_ecef_array = companionTelemetry.pos_z_ecef_array;
      this.companionTelemetryData.vel_x_ms_array = companionTelemetry.vel_x_ms_array;
      this.companionTelemetryData.rot_x_array = companionTelemetry.rot_x_array;
      this.companionTelemetryData.rot_y_array = companionTelemetry.rot_y_array;
      this.companionTelemetryData.rot_z_array = companionTelemetry.rot_z_array;
      this.companionTelemetryData.rot_w_array = companionTelemetry.rot_w_array;
      this.companionTelemetryData.solution_type_array = companionTelemetry.solution_type_array;
      this.companionTelemetryData.vision_status_array = companionTelemetry.vision_status_array;
      this.companionTelemetryData.anti_collision_status_array =
        companionTelemetry.anti_collision_status_array;
      this.companionTelemetryData.identified_obstacle_type_array =
        companionTelemetry.identified_obstacle_type_array;
      this.companionTelemetryData.engine_status_array = companionTelemetry.engine_status_array;
      this.companionTelemetryData.engine_rpm_array = companionTelemetry.engine_rpm_array;
      this.companionTelemetryData.fuel_level_percent_array =
        companionTelemetry.fuel_level_percent_array;
      this.companionTelemetryData.ecu_voltage_v_array = companionTelemetry.ecu_voltage_v_array;
      this.companionTelemetryData.water_tank_level_percent_array =
        companionTelemetry.water_tank_level_percent_array;
      this.companionTelemetryData.attachment_type_array = companionTelemetry.attachment_type_array;
      this.companionTelemetryData.attachment_status_array =
        companionTelemetry.attachment_status_array;
      this.companionTelemetryData.sprayer_left_status_array =
        companionTelemetry.sprayer_left_status_array;
      this.companionTelemetryData.sprayer_right_status_array =
        companionTelemetry.sprayer_right_status_array;
      this.companionTelemetryData.pump_pressure_bar_array =
        companionTelemetry.pump_pressure_bar_array;
      this.companionTelemetryData.flow_rate_l_min_array = companionTelemetry.flow_rate_l_min_array;
      this.companionTelemetryData.plan_name_array = companionTelemetry.plan_name_array;
      this.companionTelemetryData.plan_status_array = companionTelemetry.plan_status_array;
      this.companionTelemetryData.plan_completion_percent_array =
        companionTelemetry.plan_completion_percent_array;
      this.companionTelemetryData.anomaly_id_array = companionTelemetry.anomaly_id_array;
      this.companionTelemetryData.anomaly_type_array = companionTelemetry.anomaly_type_array;
      this.companionTelemetryData.companion_count = companionTelemetry.companion_count;
      this.lastCompanionTelemetry = new Date(latestCompanionTelemetryRecord.received_at);
    }

    // Set the specific image path for AgricobotAsset
    this.assetImagePath = '/images/agricobots/automatika-1.jpg';
  }

  public isOnline(): boolean {
    const timeoutMs = new Date(
      TimeSync.now().getTime() -
        import.meta.env.VITE_AGRICOBOTASSET_ONLINE_TIMEOUT_MINUTES * 60 * 1000,
    );

    return !!(
      (this.lastHeartbeat && this.lastHeartbeat > timeoutMs) ||
      (this.lastTelemetry && this.lastTelemetry > timeoutMs) ||
      (this.lastCamera && this.lastCamera > timeoutMs) ||
      (this.lastCompanionHeartbeat && this.lastCompanionHeartbeat > timeoutMs) ||
      (this.lastCompanionTelemetry && this.lastCompanionTelemetry > timeoutMs)
    );
  }

  private _upsertCameraDataFromPayload(payload: {
    frame_id?: string;
    timestamp?: number | null;
    format?: string | null;
    data_size_bytes?: number | null;
    image_width_px?: number | null;
    image_height_px?: number | null;
    data_payload?: string | null;
    data_hash?: string | null;
    content_type?: string | null;
    compression_ratio?: number | null;
    jpeg_quality_percent?: number | null;
  }): void {
    const frameId = payload.frame_id ?? null;
    const base: AgricobotCameraData = {
      frame_id: frameId,
      timestamp: null,
      format: null,
      data_size_bytes: null,
      image_width_px: null,
      image_height_px: null,
      data_payload: null,
      data_hash: null,
      content_type: null,
      compression_ratio: null,
      jpeg_quality_percent: null,
    };

    const index = this.cameraData.findIndex((c) => c.frame_id === frameId);
    const existing = index >= 0 ? this.cameraData[index] : base;
    const updated: AgricobotCameraData = {
      frame_id: frameId,
      timestamp: payload.timestamp ?? existing.timestamp,
      format: payload.format ?? existing.format,
      data_size_bytes: payload.data_size_bytes ?? existing.data_size_bytes,
      image_width_px: payload.image_width_px ?? existing.image_width_px,
      image_height_px: payload.image_height_px ?? existing.image_height_px,
      data_payload: payload.data_payload ?? existing.data_payload,
      data_hash: payload.data_hash ?? existing.data_hash,
      content_type: payload.content_type ?? existing.content_type,
      compression_ratio: payload.compression_ratio ?? existing.compression_ratio,
      jpeg_quality_percent: payload.jpeg_quality_percent ?? existing.jpeg_quality_percent,
    };

    if (index >= 0) {
      this.cameraData.splice(index, 1, updated);
    } else {
      this.cameraData.push(updated);
    }
  }

  public getStatus(): unknown {
    return this.heartbeatData.status;
  }

  public getAssetType(): string {
    return AssetTypes.AGRICOBOT;
  }

  public static getMessageTypes(): string[] {
    return Object.values(AgricobotMessageTypes);
  }

  public static getAgricobotAssetMessageTypesOnly(): string[] {
    // Returns message types suitable for export (excluding heavy compressed images)
    return Object.values(AgricobotOnlyMessageTypes);
  }

  public static getAgricobotAssetExportMessageTypesOnly(): string[] {
    // Returns message types suitable for export (excluding heavy compressed images)
    return Object.values(AgricobotExportableMessageTypes);
  }

  public updateInboundRecord(records: InboundRecord[]): void {
    // Sort records by timestamp in descending order to process the newest first
    // This ensures that the asset's state reflects the most recent data
    records.sort((a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime());

    this.inboundRecords = records;

    if (records.length > 0) {
      this.lastMessageReceived = new Date(records[0].received_at);
    }

    let latestHeartbeatProcessed = false;
    let latestTelemetryProcessed = false;
    let latestCameraProcessed = false;
    let latestSignalsProcessed = false;

    for (const record of records) {
      const payload = record.payload as Record<string, unknown>; // Cast once for easier access

      switch (record.message_type) {
        // AGRICOBOT ASSET
        case AgricobotMessageTypes.HEARTBEAT:
          if (!latestHeartbeatProcessed) {
            latestHeartbeatProcessed = true;
            const heartbeat = payload as AgricobotHeartbeatData;
            this.heartbeatData.frame_id = heartbeat.frame_id;
            this.heartbeatData.rbt_id = heartbeat.rbt_id;
            this.heartbeatData.rbt_model = heartbeat.rbt_model;
            this.heartbeatData.software_version = heartbeat.software_version;
            this.heartbeatData.status = heartbeat.status;
            this.lastHeartbeat = new Date(record.received_at);
          }
          break;
        case AgricobotMessageTypes.TELEMETRY_DATA:
          if (!latestTelemetryProcessed) {
            const tel = this.telemetryData; // Shorthand for AgricobotTelemetry
            const payloadObject = payload as {
              frame_id?: string;
              timestamp?: number; // Assuming timestamp can come as a number if not a full ROS Header
              pos_x_ecef?: number;
              pos_y_ecef?: number;
              pos_z_ecef?: number;
              vel_x_ms?: number;
              rot_x?: number;
              rot_y?: number;
              rot_z?: number;
              rot_w?: number;
              solution_type?: number;
              vision_status?: boolean;
              anti_collision_status?: number;
              identified_obstacle_type?: string;
              engine_status?: boolean;
              engine_rpm?: number;
              engine_torque_percent?: number;
              pto_rpm?: number;
              coolant_temp_c?: number;
              hydraulic_oil_temp_c?: number;
              fuel_level_percent?: number;
              ecu_voltage_v?: number;
              water_tank_level_percent?: number;
              attachment_type?: string;
              attachment_status?: boolean;
              sprayer_left_status?: boolean;
              sprayer_right_status?: boolean;
              pump_pressure_bar?: number;
              flow_rate_l_min?: number;
              plan_name?: string;
              plan_status?: number;
              plan_completion_percent?: number;
              anomaly_id?: number;
              anomaly_type?: string;
              TIMESTAMP?: {
                // Assuming TIMESTAMP can be part of the payload, as seen in updateFromDatastore
                FrameId?: string;
                Package?: number;
                Seq?: number;
                Stamp?: string;
              };
            };

            tel.frame_id = payloadObject.frame_id ?? tel.frame_id;
            tel.pos_x_ecef = payloadObject.pos_x_ecef ?? tel.pos_x_ecef;
            tel.pos_y_ecef = payloadObject.pos_y_ecef ?? tel.pos_y_ecef;
            tel.pos_z_ecef = payloadObject.pos_z_ecef ?? tel.pos_z_ecef;
            tel.vel_x_ms = payloadObject.vel_x_ms ?? tel.vel_x_ms;
            tel.rot_x = payloadObject.rot_x ?? tel.rot_x;
            tel.rot_y = payloadObject.rot_y ?? tel.rot_y;
            tel.rot_z = payloadObject.rot_z ?? tel.rot_z;
            tel.rot_w = payloadObject.rot_w ?? tel.rot_w;
            tel.solution_type = payloadObject.solution_type ?? tel.solution_type;
            tel.vision_status = payloadObject.vision_status ?? tel.vision_status;
            tel.anti_collision_status =
              payloadObject.anti_collision_status ?? tel.anti_collision_status;
            tel.identified_obstacle_type =
              payloadObject.identified_obstacle_type ?? tel.identified_obstacle_type;
            tel.engine_status = payloadObject.engine_status ?? tel.engine_status;
            tel.engine_rpm = payloadObject.engine_rpm ?? tel.engine_rpm;
            tel.engine_torque_percent =
              payloadObject.engine_torque_percent ?? tel.engine_torque_percent;
            tel.pto_rpm = payloadObject.pto_rpm ?? tel.pto_rpm;
            tel.coolant_temp_c = payloadObject.coolant_temp_c ?? tel.coolant_temp_c;
            tel.hydraulic_oil_temp_c =
              payloadObject.hydraulic_oil_temp_c ?? tel.hydraulic_oil_temp_c;
            tel.fuel_level_percent = payloadObject.fuel_level_percent ?? tel.fuel_level_percent;
            tel.ecu_voltage_v = payloadObject.ecu_voltage_v ?? tel.ecu_voltage_v;
            tel.water_tank_level_percent =
              payloadObject.water_tank_level_percent ?? tel.water_tank_level_percent;
            tel.attachment_type = payloadObject.attachment_type ?? tel.attachment_type;
            tel.attachment_status = payloadObject.attachment_status ?? tel.attachment_status;
            tel.sprayer_left_status = payloadObject.sprayer_left_status ?? tel.sprayer_left_status;
            tel.sprayer_right_status =
              payloadObject.sprayer_right_status ?? tel.sprayer_right_status;
            tel.pump_pressure_bar = payloadObject.pump_pressure_bar ?? tel.pump_pressure_bar;
            tel.flow_rate_l_min = payloadObject.flow_rate_l_min ?? tel.flow_rate_l_min;
            tel.plan_name = payloadObject.plan_name ?? tel.plan_name;
            tel.plan_status = payloadObject.plan_status ?? tel.plan_status;
            tel.plan_completion_percent =
              payloadObject.plan_completion_percent ?? tel.plan_completion_percent;
            tel.anomaly_id = payloadObject.anomaly_id ?? tel.anomaly_id;
            tel.anomaly_type = payloadObject.anomaly_type ?? tel.anomaly_type;

            // Handle device timestamp from ROS TelemetryData Header (std_msgs/Header)
            // payload.TIMESTAMP will have .Stamp as a string
            if (payloadObject.TIMESTAMP?.Stamp !== undefined) {
              tel.timestamp = new Date(payloadObject.TIMESTAMP.Stamp);
            } else if (payloadObject.timestamp !== undefined) {
              // Fallback to direct timestamp if Header is not present
              tel.timestamp = new Date(payloadObject.timestamp);
            }
            // Update localization FrameID from TelemetryData Header if available
            if (payloadObject.TIMESTAMP?.FrameId !== undefined) {
              tel.frame_id = payloadObject.TIMESTAMP.FrameId; // Update frame_id in telemetryData
            } else if (payloadObject.frame_id !== undefined) {
              tel.frame_id = payloadObject.frame_id; // Fallback to direct frame_id
            }

            this.lastTelemetry = new Date(record.received_at); // Update lastTelemetry when telemetry data is processed
            latestTelemetryProcessed = true; // Mark as processed to only get the latest
          }
          break;
        case AgricobotMessageTypes.COMPRESSED_IMAGE:
          if (!latestCameraProcessed) {
            const imagePayload = payload as {
              frame_id?: string;
              timestamp?: number;
              format?: string;
              data_size_bytes?: number;
              image_width_px?: number;
              image_height_px?: number;
              data_payload?: string;
              data_hash?: string;
              content_type?: string;
              compression_ratio?: number;
              jpeg_quality_percent?: number;
            };
            this._upsertCameraDataFromPayload(imagePayload);
            this.lastCamera = new Date(record.received_at); // Update lastCamera when camera data is processed
            latestCameraProcessed = true; // Mark as processed to only get the latest
          }
          break;
        // COMPANIONS
        case AgricobotMessageTypes.COMPANION_HEARTBEAT:
        case AgricobotMessageTypes.COMPANION_TELEMETRY_DATA:
          // Companion records are handled in the companion records loop
          break;
        case AgricobotMessageTypes.SIGNALS:
          // Solo la piu recente: ogni riga porta l'ULTIMO valore noto di ogni
          // segnale, quindi la piu recente contiene gia tutto e applicare
          // anche le precedenti sovrascriverebbe con valori vecchi.
          if (!latestSignalsProcessed) {
            latestSignalsProcessed = true;
            this.signals = readSignalsPayload(payload);
            this.lastSignals = new Date(record.received_at);
          }
          break;
        default:
          console.warn(
            '[AgricobotAsset] updateInboundRecord: message type not recognized:',
            record.message_type,
          );
          console.warn('[AgricobotAsset] updateInboundRecord: payload:', payload);
          break;
      }

      // If all types of messages are processed, we can stop early
      if (latestHeartbeatProcessed && latestTelemetryProcessed && latestCameraProcessed) {
        break;
      }
    }

    const companionRecords = records.filter((record) =>
      Object.values(AgricobotCompanionMessageTypes).includes(
        record.message_type as AgricobotCompanionMessageTypes,
      ),
    );

    const companionService = new AgricobotCompanionServices();
    companionService.updateCompanions(this.companions, companionRecords);
  }

  /* ===============================
   * COMPANIONS SYSTEM METHODS
  =============================== */

  private createCompanions(): void {
    const count =
      (this.companionHeartbeatData.companion_count ?? 0) +
      (this.companionTelemetryData.companion_count ?? 0);
    if (count === 0) return;
    const service = new AgricobotCompanionServices();
    this.companions = service.createCompanionsFromArray(
      this.companionHeartbeatData,
      this.companionTelemetryData,
      this.inboundRecords[0],
      this,
    );
  }

  public getCompanions(): AgricobotCompanionAsset[] {
    if (this.companions.length === 0) this.createCompanions();
    return this.companions;
  }

  public getCompanion(id: string): AgricobotCompanionAsset | null {
    if (this.companions.length === 0) this.createCompanions();
    return this.companions.find((companion) => companion.companionId === id) ?? null;
  }

  public getCompanionCount(): number {
    if (this.companions.length === 0) this.createCompanions();
    return this.companions.length;
  }

  /* ===============================
   * CAMERA RELATED METHODS
  =============================== */

  /**
   * Returns the cached URL for the latest camera image if available.
   * The URL may be a blob URL (revoked on update) or null if no image was fetched yet.
   */
  public getCameraImageUrl(): string | null {
    return this._latestCameraImageUrl;
  }

  /**
   * Fetch the latest compressed image for this asset from the `inbound` table and cache it.
   * - Filters by this asset's node_id, asset_id, device_id and message_type 'compressed_image'
   * - Converts hex-encoded `content` to binary and creates a Blob URL
   * - Updates `cameraData`, `lastCamera`, and caches the URL in `_latestCameraImageUrl`
   *
   * @returns Promise<string | null> The blob URL for the image or null if not found
   */
  public async getLatestCameraImage(preferredFrameId?: string): Promise<string | null> {
    try {
      let frameIdFilter: string | undefined = undefined;
      // If preferred frame has no data in memory, still try to fetch by it first; fallback to first available
      if (preferredFrameId) {
        frameIdFilter = preferredFrameId;
      }

      let query = supabase
        .from('inbound')
        .select(
          'id, received_at, message_type, content_type, payload, content, node_id, asset_id, device_id',
        )
        .eq('node_id', this.node_id)
        .eq('asset_id', this.asset_id)
        .eq('device_id', this.device_id)
        .eq('message_type', AgricobotMessageTypes.COMPRESSED_IMAGE)
        .order('received_at', { ascending: false });

      if (frameIdFilter) {
        query = query.contains('payload', {
          frame_id: frameIdFilter,
        } as unknown as Record<string, unknown>);
      }

      const { data, error } = await query.limit(1);

      if (error) {
        console.error('[AgricobotAsset] getLatestCameraImage: query error', error);
        return null;
      }

      // If nothing returned and we tried a specific frame, retry without frame filter as fallback
      if ((!data || data.length === 0) && frameIdFilter) {
        const fallback = await supabase
          .from('inbound')
          .select(
            'id, received_at, message_type, content_type, payload, content, node_id, asset_id, device_id',
          )
          .eq('node_id', this.node_id)
          .eq('asset_id', this.asset_id)
          .eq('device_id', this.device_id)
          .eq('message_type', AgricobotMessageTypes.COMPRESSED_IMAGE)
          .order('received_at', { ascending: false })
          .limit(1);
        if (fallback.error) {
          console.error(
            '[AgricobotAsset] getLatestCameraImage: fallback query error',
            fallback.error,
          );
          return null;
        }
        if (!fallback.data || fallback.data.length === 0) {
          return null;
        }
        // Use fallback result
        const latest = fallback.data[0] as unknown as InboundRecord & {
          content?: string | null;
          content_type?: string | null;
          payload: Record<string, unknown>;
        };
        return this._processInboundImageRecord(latest);
      }

      if (!data || data.length === 0) {
        return null;
      }

      const latest = data[0] as unknown as InboundRecord & {
        content?: string | null;
        content_type?: string | null;
        payload: Record<string, unknown>;
      };

      return this._processInboundImageRecord(latest);
    } catch (e) {
      console.error('[AgricobotAsset] getLatestCameraImage: unexpected error', e);
      return null;
    }
  }

  private _processInboundImageRecord(
    latest: InboundRecord & {
      content?: string | null;
      content_type?: string | null;
      payload: Record<string, unknown>;
    },
  ): string | null {
    // Parse payload into cameraData structure
    const p = latest.payload as {
      frame_id?: string;
      timestamp?: number; // nanoseconds since epoch or ms
      format?: string;
      data_size?: number;
      image_width_px?: number;
      image_height_px?: number;
      data_size_bytes?: number;
      content_type?: string;
      jpeg_quality?: number;
      jpeg_quality_percent?: number;
      compression_ratio?: number;
      data_hash?: string;
      width?: number;
      height?: number;
    };

    this._upsertCameraDataFromPayload({
      frame_id: p.frame_id,
      timestamp: p.timestamp != null ? p.timestamp : undefined,
      format: p.format,
      data_size_bytes: p.data_size_bytes ?? p.data_size,
      image_width_px: p.image_width_px ?? p.width,
      image_height_px: p.image_height_px ?? p.height,
      data_hash: p.data_hash,
      content_type: p.content_type ?? latest.content_type ?? undefined,
      compression_ratio: p.compression_ratio,
      jpeg_quality_percent: p.jpeg_quality_percent ?? p.jpeg_quality,
    });

    // Update timestamps on the asset
    this.lastCamera = new Date(latest.received_at);

    // Convert hex content (e.g., "\\xffd8...") to binary and create a blob URL
    let newUrl: string | null = null;
    if (latest.content) {
      try {
        const hexString = latest.content.replace(/\\x/g, '');
        const bytes = new Uint8Array(hexString.length / 2);
        for (let i = 0; i < hexString.length; i += 2) {
          bytes[i / 2] = Number.parseInt(hexString.substring(i, i + 2), 16);
        }
        const frameId = p.frame_id ?? null;
        const cam = this.cameraData.find((c) => c.frame_id === frameId);
        const contentType = cam?.content_type || 'image/jpeg';
        const blob = new Blob([bytes], { type: contentType });
        newUrl = URL.createObjectURL(blob);
      } catch (convErr) {
        console.error('[AgricobotAsset] getLatestCameraImage: hex->blob conversion error', convErr);
        newUrl = null;
      }
    }

    // Revoke previous blob URL to avoid memory leaks
    if (this._latestCameraImageUrl?.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(this._latestCameraImageUrl);
      } catch (revokeErr) {
        console.warn(
          '[AgricobotAsset] getLatestCameraImage: failed to revoke old blob URL',
          revokeErr,
        );
      }
    }

    this._latestCameraImageUrl = newUrl;
    return this._latestCameraImageUrl;
  }

  /* ===============================
   * POSITION, ORIENTATION AND VELOCITY RELATED METHODS
  =============================== */

  /**
   * Converts ECEF coordinates to WGS84 geographic coordinates
   * Uses the same iterative algorithm as BaseAsset.getCoordinates.
   * @returns [longitude, latitude] in decimal degrees, or null if position is not available
   */
  public getGeographicCoordinates(invertLngLat = false): [number, number] | null {
    if (
      this.telemetryData.pos_x_ecef == null ||
      this.telemetryData.pos_y_ecef == null ||
      this.telemetryData.pos_z_ecef == null
    ) {
      return null;
    }

    const { x, y, z } = {
      x: this.telemetryData.pos_x_ecef,
      y: this.telemetryData.pos_y_ecef,
      z: this.telemetryData.pos_z_ecef,
    };

    /**
     * A machine without a satellite fix does not send nothing: it sends a
     * sentinel. Observed in production as -9999999999 on all three axes, and
     * zeroes are the other common way of saying the same thing.
     *
     * Converting either produces a perfectly well-formed pair of coordinates
     * somewhere in the ocean, which is worse than no answer at all: the marker
     * lands in the Atlantic and the card shows a black square of open water.
     * Only a point within a few hundred kilometres of the Earth's surface can
     * be a machine in a field.
     */
    const distanceFromCentre = Math.sqrt(x * x + y * y + z * z);
    const MIN_PLAUSIBLE_M = 6_200_000;
    const MAX_PLAUSIBLE_M = 6_800_000;
    if (distanceFromCentre < MIN_PLAUSIBLE_M || distanceFromCentre > MAX_PLAUSIBLE_M) {
      return null;
    }

    const a = 6378137.0; // semi-major axis in meters
    const e2 = 0.006694379990141316; // first eccentricity squared

    // Distance from Earth's center to point projected onto XY plane
    const p = Math.sqrt(x * x + y * y);

    // Initial value for latitude
    let lat = Math.atan2(z, p * (1 - e2));

    let prevLat: number;
    // Iterative algorithm to calculate latitude (usually converges in 2-3 iterations)
    do {
      prevLat = lat;
      const sinLat = Math.sin(lat);
      const N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
      lat = Math.atan2(z + e2 * N * sinLat, p);
    } while (Math.abs(lat - prevLat) > 1e-12);

    // Calculate longitude
    const lon = Math.atan2(y, x);

    // Convert to degrees
    const lonDeg = lon * (180 / Math.PI);
    const latDeg = lat * (180 / Math.PI);

    if (invertLngLat) {
      return [latDeg, lonDeg];
    }
    return [lonDeg, latDeg];
  }

  /* ===============================
   * EXPORT, TRANSLATION AND VALUE-FORMATTING METHODS
  =============================== */

  /**
   * Exports all asset properties
   * @returns Object containing all asset properties with original property names
   */
  public exportAllProperties(): Record<string, unknown> {
    return {
      ...this.exportGenericProperties(),
      ...this.exportTelemetryProperties(),
    };
  }

  /**
   * Exports heartbeat data with the original field names as they appear in the payload
   * @returns Object containing all heartbeat properties with original payload field names
   */
  /**
   * I tipi di messaggio in cui una proprieta puo' trovarsi.
   *
   * Serve a non pagare per righe che non la contengono. Lo storico chiedeva
   * gli ultimi cento record di TUTTI i tipi: su una macchina che manda anche
   * immagini, quei cento erano in gran parte fotogrammi - che per un grafico
   * non servono a niente, e che portano i byte del JPEG dentro il payload
   * (data_payload e' un array JSON di numeri: 100 KB di immagine diventano
   * 300 KB di testo). Il grafico restava con una manciata di punti, scaricando
   * megabyte.
   */
  public messageTypesForKey(key: string): string[] {
    if (key.startsWith(SIGNAL_EXPORT_PREFIX)) {
      return [AgricobotMessageTypes.SIGNALS];
    }
    if (key in this.exportHeartbeatProperties()) {
      return [AgricobotMessageTypes.HEARTBEAT];
    }
    return [AgricobotMessageTypes.TELEMETRY_DATA];
  }

  /**
   * Il modello da mostrare: quello configurato sulla macchina, o in mancanza
   * quello che la macchina dichiara nel heartbeat.
   *
   * Un accessore e non un valore calcolato: `model` arriva dal registro dopo il
   * caricamento, e un valore calcolato su una proprieta' che Vue non osserva
   * resterebbe fermo al primo risultato.
   */
  public get modelLabel(): string | null {
    const configured = this.model?.trim();
    if (configured) return configured;
    return this.heartbeatData.rbt_model;
  }

  public exportHeartbeatProperties(): Record<string, unknown> {
    return {
      rbt_id: this.heartbeatData.rbt_id,
      // Il modello configurato vince su quello del heartbeat anche qui, in
      // "Dati Generali" e nel CSV: chi esporta vuole il modello commerciale,
      // non la stringa con cui e' stato compilato il software di bordo.
      rbt_model: this.modelLabel,
      software_version: this.heartbeatData.software_version,
      // Sotto la versione del software di bordo, la versione del servizio che
      // porta i dati in cloud: sono due cose diverse e capita di aggiornarne
      // una sola. Quando l'assistenza chiede "che versione hai", sono queste
      // due le risposte.
      injector_version: this.injectorVersion,
      // Le ore motore al posto dell'id di frame: l'id di frame e un dettaglio
      // del trasporto ROS, che nessuno legge da qui. Il contatore delle ore
      // invece decide quando si fa il tagliando. Arriva dai segnali e non dal
      // heartbeat - il heartbeat non lo porta - ma questa e la sezione in cui
      // chi apre la scheda va a cercarlo.
      engine_hours: signalNumber(this.signals, SIGNAL_IDS.engineHours),
      status: this.heartbeatData.status,
    };
  }

  /**
   * Exports all telemetry data with the original field names as they appear in the payload
   * @returns Object containing all telemetry properties with original payload field names
   */
  public exportTelemetryProperties(): Record<string, unknown> {
    // Create a base object with all telemetry fields with their original names
    const telemetryData: Record<string, unknown> = {
      // ECEF Position
      pos_x_ecef: this.telemetryData.pos_x_ecef,
      pos_y_ecef: this.telemetryData.pos_y_ecef,
      pos_z_ecef: this.telemetryData.pos_z_ecef,

      // Orientation
      rot_x: this.telemetryData.rot_x,
      rot_y: this.telemetryData.rot_y,
      rot_z: this.telemetryData.rot_z,
      rot_w: this.telemetryData.rot_w,

      // Velocity
      vel_x_ms: this.telemetryData.vel_x_ms,

      // Solution & Vision Status
      solution_type: this.telemetryData.solution_type,
      vision_status: this.telemetryData.vision_status,

      // Collision & Obstacle
      anti_collision_status: this.telemetryData.anti_collision_status,
      identified_obstacle_type: this.telemetryData.identified_obstacle_type,

      // Engine
      engine_status: this.telemetryData.engine_status,
      engine_rpm: this.telemetryData.engine_rpm,
      fuel_level_percent: this.telemetryData.fuel_level_percent,
      ecu_voltage_v: this.telemetryData.ecu_voltage_v,

      // Other
      water_tank_level_percent: this.telemetryData.water_tank_level_percent,
      attachment_status: this.telemetryData.attachment_status,
      attachment_type: this.telemetryData.attachment_type,

      // Anomaly
      anomaly_id: this.telemetryData.anomaly_id,
      anomaly_type: this.telemetryData.anomaly_type,

      // Sprayer System
      sprayer_left_status: this.telemetryData.sprayer_left_status,
      sprayer_right_status: this.telemetryData.sprayer_right_status,
      pump_pressure_bar: this.telemetryData.pump_pressure_bar,
      flow_rate_l_min: this.telemetryData.flow_rate_l_min,

      // Mission Plan
      plan_name: this.telemetryData.plan_name,
      plan_status: this.telemetryData.plan_status,
      plan_completion_percent: this.telemetryData.plan_completion_percent,
    };

    // I segnali, cosi come la macchina li ha dichiarati. Qui non c'e un elenco
    // da tenere allineato: ogni segnale con publish acceso in `signals.yaml`
    // compare da solo, con il suo id e la sua unita, anche se questo frontend
    // non sapeva che esistesse. E il motivo per cui aggiungere un parametro da
    // mandare in cloud e una riga di YAML e non un rilascio.
    //
    // Le ore motore restano fuori: hanno gia una riga loro fra i dati generali,
    // e mostrarle due volte nella stessa schermata confonde e basta.
    for (const [id, signal] of Object.entries(this.signals)) {
      if ((SIGNAL_IDS.engineHours as readonly string[]).includes(id)) continue;
      // Il valore scaduto vale null come ovunque: meglio un buco dichiarato di
      // un numero fermo da un'ora con la faccia di uno fresco.
      telemetryData[`${SIGNAL_EXPORT_PREFIX}${id}`] = signal.stale ? null : signal.value;
    }

    return telemetryData;
  }

  /**
   * Translates export keys from exportTelemetryProperties and exportGenericProperties to localized strings
   * @param key The export key to translate
   * @param t Translation function from useI18n
   * @returns The localized string for the key
   */
  public translateExportKey(
    key: string,
    t?: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    // If no translation function provided, return the key as-is
    if (!t) {
      return key;
    }

    // I segnali non passano per lo switch: il loro elenco lo decide la macchina,
    // non questo file. Si cerca una traduzione, e se non c'e si usa l'id
    // rigirato in parole - cosi un segnale nuovo compare leggibile senza che
    // nessuno lo aggiunga qui.
    if (key.startsWith(SIGNAL_EXPORT_PREFIX)) {
      const id = key.slice(SIGNAL_EXPORT_PREFIX.length);
      const translationKey = `asset.agricobots_robot.signals.${id}`;
      const translated = t(translationKey);
      return translated === translationKey ? prettifySignalId(id) : translated;
    }

    // Handle all export keys from both exportTelemetryProperties and exportGenericProperties
    switch (key) {
      // Identifiers
      case 'node_id':
        return t('asset.agricobots_robot.exportkey.node_id');
      case 'asset_id':
        return t('asset.agricobots_robot.exportkey.asset_id');
      case 'device_id':
        return t('asset.agricobots_robot.exportkey.device_id');
      case 'device_name':
        return t('asset.agricobots_robot.exportkey.device_name');

      // Generic/logic properties
      case 'lastMessageType':
        return t('asset.agricobots_robot.exportkey.last_message_type');
      case 'status':
        return t('asset.agricobots_robot.exportkey.status');
      case 'lastMessageReceived':
        return t('asset.agricobots_robot.exportkey.last_message_received');

      // Heartbeat properties
      case 'rbt_id':
        return t('asset.agricobots_robot.exportkey.rbt_id');
      case 'rbt_model':
        return t('asset.agricobots_robot.exportkey.rbt_model');
      case 'software_version':
        return t('asset.agricobots_robot.exportkey.software_version');
      case 'engine_hours':
        return t('asset.agricobots_robot.exportkey.engine_hours');
      case 'injector_version':
        return t('asset.agricobots_robot.exportkey.injector_version');
      case 'lastReceivedAt':
        return t('asset.agricobots_robot.exportkey.last_received_at');
      case 'isOnline':
        return t('asset.agricobots_robot.exportkey.is_online');

      // TelemetryData properties
      // ECEF Position
      case 'pos_x_ecef':
        return t('asset.agricobots_robot.exportkey.pos_x_ecef');
      case 'pos_y_ecef':
        return t('asset.agricobots_robot.exportkey.pos_y_ecef');
      case 'pos_z_ecef':
        return t('asset.agricobots_robot.exportkey.pos_z_ecef');

      // Orientation
      case 'rot_x':
        return t('asset.agricobots_robot.exportkey.rot_x');
      case 'rot_y':
        return t('asset.agricobots_robot.exportkey.rot_y');
      case 'rot_z':
        return t('asset.agricobots_robot.exportkey.rot_z');
      case 'rot_w':
        return t('asset.agricobots_robot.exportkey.rot_w');

      // Velocity
      case 'vel_x_ms':
        return t('asset.agricobots_robot.exportkey.vel_x_ms');

      // Solution & Vision Status
      case 'solution_type':
        return t('asset.agricobots_robot.exportkey.solution_type');
      case 'vision_status':
        return t('asset.agricobots_robot.exportkey.vision_status');

      // Collision & Obstacle
      case 'anti_collision_status':
        return t('asset.agricobots_robot.exportkey.anti_collision_status');
      case 'identified_obstacle_type':
        return t('asset.agricobots_robot.exportkey.identified_obstacle_type');

      // Engine
      case 'engine_status':
        return t('asset.agricobots_robot.exportkey.engine_status');
      case 'engine_rpm':
        return t('asset.agricobots_robot.exportkey.engine_rpm');
      case 'fuel_level_percent':
        return t('asset.agricobots_robot.exportkey.fuel_level_percent');
      case 'ecu_voltage_v':
        return t('asset.agricobots_robot.exportkey.ecu_voltage_v');

      // Other
      case 'water_tank_level_percent':
        return t('asset.agricobots_robot.exportkey.water_tank_level_percent');
      case 'attachment_status':
        return t('asset.agricobots_robot.exportkey.attachment_status');
      case 'attachment_type':
        return t('asset.agricobots_robot.exportkey.attachment_type');

      // Anomaly
      case 'anomaly_id':
        return t('asset.agricobots_robot.exportkey.anomaly_id');
      case 'anomaly_type':
        return t('asset.agricobots_robot.exportkey.anomaly_type');

      // Sprayer System
      case 'sprayer_left_status':
        return t('asset.agricobots_robot.exportkey.sprayer_left_status');
      case 'sprayer_right_status':
        return t('asset.agricobots_robot.exportkey.sprayer_right_status');
      case 'pump_pressure_bar':
        return t('asset.agricobots_robot.exportkey.pump_pressure_bar');
      case 'flow_rate_l_min':
        return t('asset.agricobots_robot.exportkey.flow_rate_l_min');

      // Mission Plan
      case 'plan_name':
        return t('asset.agricobots_robot.exportkey.plan_name');
      case 'plan_status':
        return t('asset.agricobots_robot.exportkey.plan_status');
      case 'plan_completion_percent':
        return t('asset.agricobots_robot.exportkey.plan_completion_percent');

      // CameraData properties
      case 'frame_id':
        return t('asset.agricobots_robot.exportkey.frame_id');
      case 'format':
        return t('asset.agricobots_robot.exportkey.format');
      case 'timestamp':
        return t('asset.agricobots_robot.exportkey.timestamp');
      case 'image_width_px':
        return t('asset.agricobots_robot.exportkey.image_width_px');
      case 'image_height_px':
        return t('asset.agricobots_robot.exportkey.image_height_px');
      case 'data_size_bytes':
        return t('asset.agricobots_robot.exportkey.data_size_bytes');
      case 'jpeg_quality_percent':
        return t('asset.agricobots_robot.exportkey.jpeg_quality_percent');
      case 'compression_ratio':
        return t('asset.agricobots_robot.exportkey.compression_ratio');
      case 'data_hash':
        return t('asset.agricobots_robot.exportkey.data_hash');
      case 'content_type':
        return t('asset.agricobots_robot.exportkey.content_type');

      // Default fallback
      default:
        console.error('key not recognized in translateExportKey, Default fallback for key:', key);
        return t(`asset.agricobots_robot.exportkey.${key}`) || key;
    }
  }

  /**
   * Formats export values from exportTelemetryProperties and exportGenericProperties
   * @param key The export key
   * @param value The value to format
   * @param t Translation function from useI18n (optional)
   * @returns Formatted value as string
   */
  public formatExportValue(
    key: string,
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    // Default values for translations
    const notAvailable = t ? t('common.not_available') : 'N/A';
    const yes = t ? t('common.yes') : 'Yes';
    const no = t ? t('common.no') : 'No';
    const none = t ? t('common.none') : 'None';

    // Return notAvailable for null or undefined values
    if (value === null || value === undefined) {
      return notAvailable;
    }

    // Un segnale si formatta con l'unita che la macchina ha dichiarato nel suo
    // file, non con una decisa qui: se non la dichiara non si scrive niente,
    // che e meglio di inventarla.
    //
    // La spaziatura sta dentro l'unita, come nel file dei segnali: "°C" e "%"
    // si attaccano al numero, " bar" e " m/s" arrivano con il loro spazio. Chi
    // scrive il file decide come si legge, e qui non si indovina.
    if (key.startsWith(SIGNAL_EXPORT_PREFIX)) {
      if (typeof value !== 'number') return String(value);
      const id = key.slice(SIGNAL_EXPORT_PREFIX.length);
      const signal = this.signals[id];
      // Un interruttore si legge a parole, come lo stato degli irroratori: "1"
      // non dice niente a chi sta guardando la macchina, "Acceso" si.
      //
      // Lo dichiara la macchina; l'elenco e' il ripiego per le macchine con un
      // injector che quella dichiarazione non la scrive ancora.
      if (signal?.bool === true || BOOLEAN_SIGNAL_IDS.includes(id)) {
        return t(`asset.agricobots_robot.mappings.boolean.${value === 0 ? 0 : 1}`);
      }
      return `${value}${signal?.unit ?? ''}`;
    }

    // Format values based on key
    switch (key) {
      // Identifiers
      case 'node_id':
      case 'asset_id':
      case 'device_id':
      case 'device_name':
        return String(value);

      // Generic/logic properties
      case 'lastMessageType':
        return String(value);
      case 'status':
        return this._formatRbtStatus(value, t);
      case 'lastMessageReceived':
        return this.lastMessageReceived?.toUTCString() ?? notAvailable;

      // Heartbeat properties
      // Missing (because already added)
      case 'rbt_id':
      case 'rbt_model':
      case 'software_version':
        return String(value);

      // Il binario si dichiara come "1.14.1+f99cd8f": dopo il piu' c'e' il
      // commit da cui e' stato costruito, che il Makefile ci attacca con
      // -ldflags. E' la metadata di build del semver, e in database resta
      // intera - e' quella che permette di dire da quale commit viene un
      // pacchetto installato in campo. Qui pero' si legge una versione, non si
      // rintraccia una build: si mostra solo la parte che l'operatore
      // riconosce.
      case 'injector_version':
        return String(value).split('+')[0];

      // Ore motore al decimo, troncate e non arrotondate: un contatore di ore
      // che sale non deve mai mostrare piu ore di quante ne siano passate.
      case 'engine_hours':
        return typeof value === 'number'
          ? `${(Math.trunc(value * 10) / 10).toFixed(1)} h`
          : notAvailable;

      case 'lastReceivedAt':
        return value instanceof Date ? value.toLocaleString() : String(value);

      case 'isOnline':
        return formatBooleanStatus(value as boolean, yes, no);

      // TelemetryData properties
      // ECEF Position
      case 'pos_x_ecef':
      case 'pos_y_ecef':
      case 'pos_z_ecef':
        return formatDecimal(value as number, 3);

      // Orientation
      case 'rot_x':
      case 'rot_y':
      case 'rot_z':
      case 'rot_w':
        return formatDecimal(value as number, 4);

      // Velocity
      case 'vel_x_ms':
        return `${formatDecimal(value as number, 2)} m/s`;

      // Solution & Vision Status
      case 'solution_type':
        return this._formatRbtSolutionType(value, t);

      case 'vision_status':
        return this._formatRbtVisionStatus(value, t);

      // Collision & Obstacle
      case 'anti_collision_status':
        return this._formatRbtAntiCollisionStatus(value, t);

      case 'identified_obstacle_type':
        return String(value) || none;

      // Engine
      case 'engine_status':
        return this._formatRbtEngineStatus(value, t);

      case 'engine_rpm':
        return formatRpm(value as number);

      case 'fuel_level_percent':
        return formatPercentage(value as number, 1);

      case 'ecu_voltage_v':
        return formatVoltage(value as number);

      // Other
      case 'water_tank_level_percent':
        return formatPercentage(value as number, 1);

      case 'attachment_status':
        return this._formatRbtAttachmentStatus(value, t);
      case 'attachment_type':
        return String(value) || none;

      // Anomaly
      case 'anomaly_id':
        return String(value);

      case 'anomaly_type':
        return String(value) || none;

      // Sprayer System
      case 'sprayer_left_status':
      case 'sprayer_right_status':
        return this._formatRbtSprayerLeftStatus(value, t);

      case 'pump_pressure_bar':
        return `${formatDecimal(value as number, 1)} bar`;
      case 'flow_rate_l_min':
        return `${formatDecimal(value as number, 1)} L/min`;

      // Mission Plan
      case 'plan_name':
        return String(value) || none;

      case 'plan_status':
        return this._formatRbtPlanStatus(value, t);

      case 'plan_completion_percent':
        return formatPercentage(value as number, 1);

      // CameraData properties
      case 'frame_id':
      case 'format':
        return String(value);

      case 'timestamp':
        return value instanceof Date ? value.toLocaleString() : String(value);

      case 'image_width_px':
      case 'image_height_px':
      case 'data_size_bytes':
        return String(value);

      case 'jpeg_quality_percent':
        return value ? `${value}%` : notAvailable;

      case 'compression_ratio':
        return value ? `${formatDecimal(value as number, 2)}:1` : notAvailable;

      case 'data_hash':
        return String(value);
      case 'content_type':
        return String(value);

      // Default fallback
      default:
        console.error(
          'key not recognized in formatExportValue, Default fallback for key:',
          key,
          'value:',
          value,
        );
        return formatSmartValue(value);
    }
  }

  public isCalculatedProperty(key: string): boolean {
    switch (key) {
      case 'status':
        return true;
      default:
        return false;
    }
  }

  _formatRbtStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    const integerValue = Number.parseInt(value as string);
    switch (integerValue) {
      case 0:
        return t('asset.agricobots_robot.mappings.status.0');
      case 1:
        return t('asset.agricobots_robot.mappings.status.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtSolutionType(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
        return t('asset.agricobots_robot.mappings.solution_type.0');
      case 1:
        return t('asset.agricobots_robot.mappings.solution_type.1');
      case 2:
        return t('asset.agricobots_robot.mappings.solution_type.2');
      case 3:
        return t('asset.agricobots_robot.mappings.solution_type.3');
      case 4:
        return t('asset.agricobots_robot.mappings.solution_type.4');
      case 5:
        return t('asset.agricobots_robot.mappings.solution_type.5');
      case 6:
        return t('asset.agricobots_robot.mappings.solution_type.6');
      case 7:
        return t('asset.agricobots_robot.mappings.solution_type.7');
      case 8:
        return t('asset.agricobots_robot.mappings.solution_type.8');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtVisionStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
      case false:
        return t('asset.agricobots_robot.mappings.vision_status.0');
      case 1:
      case true:
        return t('asset.agricobots_robot.mappings.vision_status.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtAntiCollisionStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
        return t('asset.agricobots_robot.mappings.anti_collision.0');
      case 1:
        return t('asset.agricobots_robot.mappings.anti_collision.1');
      case 2:
        return t('asset.agricobots_robot.mappings.anti_collision.2');
      case 3:
        return t('asset.agricobots_robot.mappings.anti_collision.3');
      case 4:
        return t('asset.agricobots_robot.mappings.anti_collision.4');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtEngineStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
      case false:
        return t('asset.agricobots_robot.mappings.engine_status.0');
      case 1:
      case true:
        return t('asset.agricobots_robot.mappings.engine_status.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtAttachmentStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
      case false:
        return t('asset.agricobots_robot.mappings.attachment_status.0');
      case 1:
      case true:
        return t('asset.agricobots_robot.mappings.attachment_status.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtSprayerLeftStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
      case false:
        return t('asset.agricobots_robot.mappings.sprayer_left.0');
      case 1:
      case true:
        return t('asset.agricobots_robot.mappings.sprayer_left.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtSprayerRightStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
      case false:
        return t('asset.agricobots_robot.mappings.sprayer_left.0');
      case 1:
      case true:
        return t('asset.agricobots_robot.mappings.sprayer_left.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtPlanStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    const integerValue = Number.parseInt(value as string);
    switch (integerValue) {
      case 0:
        return t('asset.agricobots_robot.mappings.plan_status.0');
      case 1:
        return t('asset.agricobots_robot.mappings.plan_status.1');
      case 2:
        return t('asset.agricobots_robot.mappings.plan_status.2');
      case 3:
        return t('asset.agricobots_robot.mappings.plan_status.3');
      case 4:
        return t('asset.agricobots_robot.mappings.plan_status.4');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  /* ===============================
   * UI HELPER METHODS
  =============================== */

  public isPropertyChartable(key: string): boolean {
    switch (key) {
      case 'vel_x_ms':
      case 'solution_type':
      case 'vision_status':
      case 'engine_rpm':
      case 'fuel_level_percent':
      case 'ecu_voltage_v':
      case 'water_tank_level_percent':
      case 'attachment_status':
      case 'sprayer_left_status':
      case 'sprayer_right_status':
      case 'pump_pressure_bar':
      case 'flow_rate_l_min':
        return true;
      default:
        return false;
    }
  }
}
